import React, { useState, useEffect, useRef, useMemo } from 'react';
import { base44 } from '@/api/base44Client';
import { useQuery } from '@tanstack/react-query';
import { motion, AnimatePresence } from 'framer-motion';
import { Loader2, ChevronRight, ShoppingCart, CheckCircle2, Award, CalendarClock } from 'lucide-react';
import { format, startOfMonth, endOfMonth, addDays } from 'date-fns';
import { useNavigate, useSearchParams } from 'react-router-dom';
import BookingStepRigSelection from '@/components/booking/BookingStepRigSelection';
import { BookingConfigProvider, useBookingConfig } from '@/lib/BookingConfigContext';
import BookingStepRigPicker from '@/components/booking/BookingStepRigPicker';
import BookingStepDateTime from '@/components/booking/BookingStepDateTime';
import BookingStepDetails from '@/components/booking/BookingStepDetails';
import { useCart } from '@/lib/CartContext';
import { useSiteSeo } from '@/lib/useSiteSeo';
import { usePageMedia } from '@/lib/usePageMedia';
import ShareButtons from '@/components/shop/ShareButtons';
import { trackAddBooking } from '@/lib/analytics';
import WaitlistJoinModal from '@/components/booking/WaitlistJoinModal';
import { useBookingAvailability } from '@/hooks/useBookingAvailability';

// Steps: 1=Experience, 2=Sim Rig (all experiences), 3=Date&Time, 4=Details
const STEPS = ['SELECT EXPERIENCE', 'SELECT SIM RIG', 'DATE & TIME', 'YOUR DETAILS'];

function BookingsInner() {
  const { get } = usePageMedia();
  const heroImg = get('bookings', 'hero');
  useSiteSeo('experiences', {
    title: 'Book a Sim Racing Experience',
    description: 'Book a sim racing session at our Bromborough facility. Adult and junior sim racing experiences on pro-spec rigs. Gift vouchers available.',
  });

  const [experienceType, setExperienceType] = useState('');
  const [rigType, setRigType] = useState('');
  const [selectedDate, setSelectedDate] = useState(null);
  const [timeSlot, setTimeSlot] = useState('');
  const [formData, setFormData] = useState({ customer_name: '', customer_email: '', customer_phone: '', notes: '', referral_source: '' });
  const [addedToCart, setAddedToCart] = useState(false);
  const [loggedInUser, setLoggedInUser] = useState(null);
  const [step, setStep] = useState(1);
  const [addingToCart, setAddingToCart] = useState(false);
  const [waitlistSlot, setWaitlistSlot] = useState(null);
  const [redeemLoyalty, setRedeemLoyalty] = useState(false);
  const [loyaltyRedeemed, setLoyaltyRedeemed] = useState(false);
  const [redemptionError, setRedemptionError] = useState('');
  const { addBookingToCart } = useCart();
  const navigate = useNavigate();
  const { experiences, rigs } = useBookingConfig();

  const { data: myBookings = [] } = useQuery({
    queryKey: ['loyalty-bookings-booking', loggedInUser?.email],
    queryFn: () => base44.entities.Booking.filter({ customer_email: loggedInUser.email }, '-date', 500),
    enabled: !!loggedInUser?.email,
  });
  const loyaltyBalance = React.useMemo(() => {
    const qualifying = myBookings.filter((b) => (b.status === 'confirmed' || b.status === 'completed') && !b.is_loyalty_free).length;
    const earned = Math.floor(qualifying / 20);
    const redeemed = myBookings.filter((b) => b.is_loyalty_free).length;
    return Math.max(0, earned - redeemed);
  }, [myBookings]);

  const allowedRigs = experiences[experienceType]?.allowed_rigs || [];
  const totalSteps = STEPS.length;

  useEffect(() => {
    base44.auth.isAuthenticated().then(async (authed) => {
      if (authed) {
        const user = await base44.auth.me();
        setLoggedInUser(user);
        setFormData(prev => ({
          ...prev,
          customer_name: [user.first_name, user.last_name].filter(Boolean).join(' ').trim() || user.full_name || '',
          customer_email: user.email || '',
          customer_phone: user.phone || '',
        }));
      }
    });
  }, []);

  // Deep-link preview: /bookings?rig=<key> preselects an adult experience and this rig
  const [searchParams] = useSearchParams();
  const previewRig = searchParams.get('rig');
  const didInitPreview = useRef(false);
  useEffect(() => {
    if (didInitPreview.current) return;
    if (!previewRig || !experiences) return;
    const matchingKey = Object.entries(experiences).find(([, v]) => Array.isArray(v?.allowed_rigs) && v.allowed_rigs.includes(previewRig))?.[0];
    if (!matchingKey) return;
    didInitPreview.current = true;
    const previewDate = searchParams.get('date');
    const previewSlot = searchParams.get('slot');
    setExperienceType(matchingKey);
    setRigType(previewRig);
    if (previewDate) setSelectedDate(new Date(previewDate + 'T00:00:00'));
    if (previewSlot) setTimeSlot(previewSlot);
    setStep(previewDate && previewSlot ? 3 : 2);
  }, [previewRig, experiences]);

  const [visibleMonth, setVisibleMonth] = useState(new Date());
  const { slotRemainingForRig, dayHint, nextAvailableForRig, slots, openDays, bookingsPaused } = useBookingAvailability(visibleMonth);

  const slotRemaining = useMemo(() => {
    if (!selectedDate || !rigType) return {};
    return slotRemainingForRig(format(selectedDate, 'yyyy-MM-dd'), rigType);
  }, [selectedDate, rigType, slotRemainingForRig]);

  // Calendar busy/open hint date arrays for the visible month
  const dayHintArrays = useMemo(() => {
    if (!visibleMonth) return { open: [], limited: [], full: [] };
    const monthStart = startOfMonth(visibleMonth);
    const monthEnd = endOfMonth(visibleMonth);
    const open = [], limited = [], full = [];
    for (let d = new Date(monthStart); d <= monthEnd; d = addDays(d, 1)) {
      const hint = dayHint(format(d, 'yyyy-MM-dd'));
      if (hint === 'closed') continue;
      if (hint === 'full') full.push(new Date(d));
      else if (hint === 'limited') limited.push(new Date(d));
      else open.push(new Date(d));
    }
    return { open, limited, full };
  }, [visibleMonth, dayHint]);

  // Next available date per rig (for the rig picker "next available" chip)
  const rigNextAvailable = useMemo(() => {
    if (!nextAvailableForRig || !rigs) return {};
    const map = {};
    for (const rigKey of Object.keys(rigs)) {
      map[rigKey] = nextAvailableForRig(rigKey);
    }
    return map;
  }, [nextAvailableForRig, rigs]);

  const handleAddToCart = async () => {
    const exp = experiences[experienceType];
    setAddingToCart(true);
    trackAddBooking({
      experience_type: experienceType,
      experience_name: exp?.name || experienceType,
      rig_type: rigType,
      price: exp?.price || 0,
    });
    await addBookingToCart({
      experience_type: experienceType,
      experience_name: exp?.name || experienceType,
      rig_type: rigType,
      date: format(selectedDate, 'yyyy-MM-dd'),
      time_slot: timeSlot,
      duration: 1,
      price: exp?.price || 0,
      customer_name: formData.customer_name,
      customer_email: formData.customer_email,
      customer_phone: formData.customer_phone,
      notes: formData.notes,
      referral_source: formData.referral_source || '',
    });
    setAddingToCart(false);
    setAddedToCart(true);
  };

  const handleRedeemLoyalty = async () => {
    setAddingToCart(true);
    setRedemptionError('');
    try {
      const res = await base44.functions.invoke('redeemLoyaltySession', {
        experienceType,
        rigType,
        date: format(selectedDate, 'yyyy-MM-dd'),
        timeSlot,
        customerName: formData.customer_name,
        customerPhone: formData.customer_phone,
        notes: formData.notes,
        duration: 1,
      });
      setAddingToCart(false);
      if (res.data?.success) {
        setLoyaltyRedeemed(true);
        setAddedToCart(true);
      } else {
        setRedemptionError(res.data?.error || 'Failed to redeem your free session. Please try again or contact us.');
      }
    } catch (e) {
      setAddingToCart(false);
      setRedemptionError('Failed to redeem your free session. Please try again or contact us.');
    }
  };

  const handleFormChange = (key, value) => setFormData(prev => ({ ...prev, [key]: value }));

  const handleExperienceSelect = (type) => {
    setExperienceType(type);
    setRigType('');
    setSelectedDate(null);
    setTimeSlot('');
  };

  const handleRigSelect = (rig) => setRigType(rig);

  const handleNextStep = () => {
    // If the selected experience has exactly one allowed rig, auto-select it
    if (step === 1 && allowedRigs.length === 1) {
      setRigType(allowedRigs[0]);
    }
    setStep(s => s + 1);
  };

  const canProceed = () => {
    if (step === 1) return !!experienceType;
    if (step === 2) return !!rigType;
    if (step === 3) return !!selectedDate && !!timeSlot;
    if (step === 4) return !!(formData.customer_name && formData.customer_email);
    return false;
  };

  const isDetailsStep = step === 4;

  const renderStep = () => {
    if (step === 1) {
      return <BookingStepRigSelection experienceType={experienceType} onSelect={handleExperienceSelect} />;
    }
    if (step === 2) {
      return <BookingStepRigPicker rigType={rigType} onSelect={handleRigSelect} allowedRigs={allowedRigs} rigNextAvailable={rigNextAvailable} />;
    }
    if (step === 3) {
      return (
        <BookingStepDateTime
          experienceType={experienceType}
          selectedDate={selectedDate}
          onSelectDate={setSelectedDate}
          timeSlot={timeSlot}
          onSelectSlot={setTimeSlot}
          slotRemaining={slotRemaining}
          dayHints={dayHintArrays}
          visibleMonth={visibleMonth}
          onMonthChange={setVisibleMonth}
          onWaitlist={setWaitlistSlot}
          slots={slots}
          openDays={openDays}
        />
      );
    }
    if (step === 4) {
      return (
        <BookingStepDetails
          formData={formData}
          onChange={handleFormChange}
          experienceType={experienceType}
          selectedDate={selectedDate}
          timeSlot={timeSlot}
          loggedInUser={loggedInUser}
        />
      );
    }
  };

  const exp = experiences[experienceType];

  if (addedToCart) {
    return (
      <div className="min-h-screen bg-[#0d0d0f] pt-16 flex items-center justify-center px-6">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          className="w-full max-w-lg text-center"
        >
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ delay: 0.2, type: 'spring', stiffness: 200 }}
            className="w-20 h-20 bg-primary flex items-center justify-center mx-auto mb-8"
          >
            <CheckCircle2 className="w-10 h-10 text-white" />
          </motion.div>
          <p className="text-primary text-xs font-bold tracking-widest mb-2">{loyaltyRedeemed ? 'LOYALTY REDEEMED' : 'ADDED TO CART'}</p>
          <h1 className="text-white font-black text-3xl uppercase mb-3">{loyaltyRedeemed ? 'Free Session Booked!' : 'Session Added!'}</h1>
          <p className="text-white/40 text-sm mb-8">
            {loyaltyRedeemed ? (
              <>Your free loyalty <span className="text-white">{exp?.name}</span> on{' '}
              <span className="text-white">{selectedDate ? format(selectedDate, 'dd MMM yyyy') : ''}</span> at{' '}
              <span className="text-white">{timeSlot}</span> is confirmed.</>
            ) : (
              <>Your <span className="text-white">{exp?.name}</span> on{' '}
              <span className="text-white">{selectedDate ? format(selectedDate, 'dd MMM yyyy') : ''}</span> at{' '}
              <span className="text-white">{timeSlot}</span> is in your cart.</>
            )}
          </p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <button
              onClick={() => { setAddedToCart(false); setLoyaltyRedeemed(false); setRedeemLoyalty(false); setStep(1); setExperienceType(''); setRigType(''); setSelectedDate(null); setTimeSlot('');               setFormData({ customer_name: '', customer_email: '', customer_phone: '', notes: '', referral_source: '' }); }}
              className="bg-primary/10 border border-primary/40 hover:bg-primary hover:border-primary text-white font-bold text-xs tracking-widest px-6 py-3 transition-colors"
            >
              ADD ANOTHER SESSION
            </button>
            <button
              onClick={() => navigate(loyaltyRedeemed ? '/account' : '/cart')}
              className="bg-primary hover:bg-primary/90 text-white font-bold text-xs tracking-widest px-8 py-3.5 transition-all flex items-center gap-2 justify-center"
            >
              {loyaltyRedeemed ? 'VIEW MY BOOKINGS' : <><ShoppingCart className="w-4 h-4" /> VIEW CART & CHECKOUT</>}
            </button>
          </div>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0d0d0f] pt-16">
      {/* Header */}
      <section className="relative overflow-hidden py-20 px-6 lg:px-10 border-b border-white/5">
        {heroImg && (
          <>
            <img src={heroImg} alt="" className="absolute inset-0 w-full h-full object-cover opacity-30" />
            <div className="absolute inset-0 bg-gradient-to-b from-[#0d0d0f]/70 to-[#0d0d0f]" />
          </>
        )}
        <div className="relative max-w-5xl mx-auto">
          <p className="text-primary text-xs font-bold tracking-widest mb-3">BOOK A SESSION</p>
          <h1 className="text-white font-black text-4xl md:text-6xl uppercase leading-tight mb-3">
            BOOK A <span className="text-primary">SESSION</span>
          </h1>
          <p className="text-white/40 text-sm max-w-lg">
            Select your experience, pick a time, and get behind the wheel at our Bromborough facility.
          </p>
          <div className="mt-5">
            <ShareButtons title="Book a Sim Racing Experience at Motion Reality" />
          </div>
        </div>
      </section>

      {/* Bookings paused banner */}
      {bookingsPaused && (
        <div className="bg-primary/10 border-b border-primary/30">
          <div className="max-w-5xl mx-auto px-6 lg:px-10 py-4 flex items-start gap-3">
            <CalendarClock className="w-5 h-5 text-primary shrink-0 mt-0.5" />
            <div>
              <p className="text-primary text-xs font-bold tracking-widest mb-0.5">CURRENT SLOTS FULLY BOOKED</p>
              <p className="text-white/60 text-sm">
                All launch sessions are currently locked out. Tap any date and time below to join the priority waitlist for early access and cancellation alerts.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Step bar */}
      <div className="border-b border-white/5 bg-[#0d0d0f] sticky top-[var(--header-offset)] z-20">
        <div className="max-w-5xl mx-auto px-6 lg:px-10 flex items-center gap-0 overflow-x-auto">
          {STEPS.map((label, i) => (
            <React.Fragment key={label}>
              <button
                onClick={() => { if (i + 1 < step) setStep(i + 1); }}
                className={`flex items-center gap-2.5 py-4 px-1 text-xs font-bold tracking-wider transition-colors whitespace-nowrap ${
                  step === i + 1 ? 'text-primary' : step > i + 1 ? 'text-white/40 hover:text-white' : 'text-white/15'
                }`}
              >
                <span className={`w-5 h-5 flex items-center justify-center text-[10px] font-black shrink-0 ${
                  step === i + 1 ? 'bg-primary text-white' : step > i + 1 ? 'bg-white/10 text-white/40' : 'bg-white/5 text-white/15'
                }`}>
                  {i + 1}
                </span>
                {label}
              </button>
              {i < STEPS.length - 1 && <ChevronRight className="w-4 h-4 text-white/10 mx-2 shrink-0" />}
            </React.Fragment>
          ))}
        </div>
      </div>

      {/* Content */}
      <section className="px-6 lg:px-10 py-10 max-w-5xl mx-auto">
        <AnimatePresence mode="wait">
          {renderStep()}
        </AnimatePresence>

        {isDetailsStep && loyaltyBalance > 0 && (
          <>
          <div className="mt-6 flex items-center justify-between border border-primary/30 bg-primary/5 p-4">
            <div className="flex items-center gap-3">
              <Award className="w-5 h-5 text-primary" />
              <div>
                <p className="text-white text-sm font-bold">Loyalty reward available</p>
                <p className="text-white/40 text-xs">You have {loyaltyBalance} free session(s) to redeem.</p>
              </div>
            </div>
            <button
              type="button"
              onClick={() => { setRedeemLoyalty((r) => !r); setRedemptionError(''); }}
              className={`text-xs font-bold tracking-widest px-4 py-2.5 transition-all ${
                redeemLoyalty ? 'bg-primary text-white' : 'border border-primary/40 text-primary hover:bg-primary/10'
              }`}
            >
              {redeemLoyalty ? 'REDEEMING ✓' : 'USE FREE SESSION'}
            </button>
          </div>
          {redemptionError && (
            <p className="text-red-400 text-xs bg-red-500/10 border border-red-500/20 px-4 py-3 mt-4">{redemptionError}</p>
          )}
          </>
        )}

        {/* Nav */}
        <div className="flex items-center justify-between mt-10 pt-8 border-t border-white/5">
          {step > 1 ? (
            <button onClick={() => setStep(step - 1)}
              className="bg-primary/10 border border-primary/40 hover:bg-primary hover:border-primary text-white font-bold text-xs tracking-widest px-6 py-3 transition-colors">
              ← BACK
            </button>
          ) : <div />}

          {!isDetailsStep ? (
            <button
              onClick={handleNextStep}
              disabled={!canProceed()}
              className="bg-primary hover:bg-primary/90 text-white font-bold text-xs tracking-widest px-8 py-3.5 transition-all disabled:opacity-30 disabled:cursor-not-allowed flex items-center gap-2"
            >
              CONTINUE <ChevronRight className="w-4 h-4" />
            </button>
          ) : (
            <button
              onClick={redeemLoyalty ? handleRedeemLoyalty : handleAddToCart}
              disabled={!canProceed() || addingToCart}
              className="bg-primary hover:bg-primary/90 text-white font-bold text-xs tracking-widest px-8 py-3.5 transition-all disabled:opacity-30 disabled:cursor-not-allowed flex items-center gap-2"
            >
              {addingToCart ? <Loader2 className="w-4 h-4 animate-spin" /> : redeemLoyalty ? <Award className="w-4 h-4" /> : <ShoppingCart className="w-4 h-4" />}
              {redeemLoyalty ? 'REDEEM FREE SESSION' : 'ADD TO CART'}
            </button>
          )}
        </div>
      </section>
      <WaitlistJoinModal
        open={!!waitlistSlot}
        slot={waitlistSlot}
        date={selectedDate}
        rigType={rigType}
        experienceType={experienceType}
        loggedInUser={loggedInUser}
        onClose={() => setWaitlistSlot(null)}
      />
    </div>
  );
}

export default function Bookings() {
  return (
    <BookingConfigProvider>
      <BookingsInner />
    </BookingConfigProvider>
  );
}